import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Navigate, Route, Routes } from 'react-router-dom';
import axios from 'axios';
import { AppProvider } from './contexts/AppContext';
import AppLayout from './layouts/AppLayout';
import { queryClient } from './lib/query';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useGetMe } from './services/query/auth/auth';

import LoginPage from './pages/auth/login/LoginPage';
import ForgotPasswordPage from './pages/auth/forgot-password/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/reset-password/ResetPasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ContactCentrePage from './pages/contact-center/ContactCentrePage';
import PatientsListPage from './pages/patient-list/PatientsListPage';
import PatientProfilePage from './pages/patient-profile/PatientProfilePage';
import AppointmentCalendarPage from './pages/appointment-calender/AppointmentCalendarPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import EducationPage from './pages/education/EducationPage';
import RemindersPage from './pages/reminder/RemindersPage';
import SettingsPage from './pages/settings/SettingsPage';
import CallHistoryPage from './pages/call-history/CallHistoryPage';

function ProtectedApp() {
  const { error } = useGetMe();

  if (error && axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      try {
        window.localStorage.removeItem('access_token');
      } catch {
        // ignore
      }
      return <Navigate to="/login" replace />;
    }
  }

  return (
    <ProtectedRoute>
      <AppProvider>
        <AppLayout />
      </AppProvider>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route path="/" element={<ProtectedApp />}>
          <Route index element={<Navigate to="dashboard" replace />} />

          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="calls" element={<ContactCentrePage />} />
          <Route path="calls/history" element={<CallHistoryPage />} />
          <Route path="patients" element={<PatientsListPage />} />
          <Route path="patients/profile" element={<PatientProfilePage />} />
          <Route path="patients/profile/:id" element={<PatientProfilePage />} />
          <Route path="appointments" element={<AppointmentCalendarPage />} />
          <Route path="education" element={<EducationPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="reminders" element={<RemindersPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;